import { Component, CUSTOM_ELEMENTS_SCHEMA, Inject, NO_ERRORS_SCHEMA, PLATFORM_ID } from '@angular/core';
import {
   FormControl,
   FormGroup,
   FormsModule,
   ReactiveFormsModule,
   UntypedFormBuilder,
   UntypedFormGroup,
   Validators
} from '@angular/forms';
import { TrabalheConosco } from '../model/integracao';
import { NgxMaskDirective, NgxMaskPipe, provideNgxMask } from 'ngx-mask';
import { ToastrService } from 'ngx-toastr';
import { ValidatorsService } from '../utils/utils.component';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { NavigationEnd, Router } from '@angular/router';
import { TitleComponent } from "../components/title/title.component";
import { ContatoService } from '../services/contato.service';
import { Title } from '@angular/platform-browser';

@Component({
   selector: 'app-trabalhe-conosco',
   standalone: true,
   imports: [ReactiveFormsModule, FormsModule, NgxMaskDirective, NgxMaskPipe, CommonModule, TitleComponent],
   templateUrl: './trabalhe-conosco.component.html',
   styleUrl: './trabalhe-conosco.component.scss',
   providers: [provideNgxMask()],
   schemas: [CUSTOM_ELEMENTS_SCHEMA, NO_ERRORS_SCHEMA]
})
export class TrabalheConoscoComponent {
   isLoading: boolean = false;
   submitted: boolean = false;
   validate!: string;
   trabalheConosco!: TrabalheConosco;
   form!: FormGroup<{
      nome: FormControl<string>;
      telefone: FormControl<string>;
      email: FormControl<string>;
      vaga: FormControl<string>;
   }>;

   constructor(
      private contatoService: ContatoService,
      private fb: UntypedFormBuilder,
      public validator: ValidatorsService,
      private toastr: ToastrService,
      @Inject(PLATFORM_ID) private platformId: Object,
      private router: Router,
      private titleService: Title
   ) {}

   ngOnInit(): void {
      this.titleService.setTitle('Trabalhe Conosco - Cancian Imóveis')
      this.form = this.fb.group({
         nome: ['', [Validators.required, Validators.minLength(4), Validators.maxLength(50)]],
         email: ['', [Validators.required, Validators.minLength(4), Validators.maxLength(50)]],
         telefone: ['', [Validators.required, Validators.required, Validators.minLength(11), Validators.maxLength(11)]],
         vaga: ['', [Validators.min(1), Validators.required]]
      });

      if (isPlatformBrowser(this.platformId)) {
         this.checkMobile();
         if (typeof window !== 'undefined') {
            window.addEventListener('resize', this.checkMobile.bind(this));
         }

         // 🔹 Garante que o scroll acontece depois da renderização
         setTimeout(() => {
            window.scrollTo(0, 0);
         });

         // 🔹 Faz o scroll ao navegar entre páginas
         this.router.events.subscribe((event) => {
            if (event instanceof NavigationEnd) {
               setTimeout(() => {
                  window.scrollTo(0, 0);
               });
            }
         });
      }
   }

   mobile: boolean = false;
   checkMobile() {
      if (typeof window !== 'undefined') {
         this.mobile = window.innerWidth < 935;
      }
   }

   async enviar() {
      this.submitted = true;

      const email = this.form.get('email')?.value;
      const caracter = email.includes('@');
      if (!caracter) {
         this.toastr.warning('Email inválido.', 'Ops!');
      }

      // Verifica validade do formulário e se o arquivo foi enviado
      if (this.form.valid && this.selectedFile) {
         try {
            this.isLoading = true;

            // Monta o FormData com os campos e o arquivo
            const formData = new FormData();
            formData.append('nome', this.form.get('nome')?.value);
            formData.append('email', this.form.get('email')?.value);
            formData.append('telefone', this.form.get('telefone')?.value);
            formData.append('cargoInteresse', this.form.get('vaga')?.value);
            formData.append('curriculo', this.selectedFile); // Aqui adiciona o arquivo

            // Envia os dados (supondo que seu service aceite FormData)
            await this.contatoService.enviarTrabalhoConosco(formData);

            this.toastr.success('Enviado com sucesso', 'Sucesso');
         } catch (e) {
            this.toastr.error('Um erro ocorreu, tente novamente mais tarde', 'Erro');
            console.error(e);
         } finally {
            this.form.reset();
            this.selectedFile = null;
            this.submitted = false;
            this.isLoading = false;
         }
      } else {
         if (!this.selectedFile) {
            this.toastr.warning('Por favor, anexe um arquivo PDF.', 'Ops!');
         }
         this.form.markAllAsTouched();
      }
   }
   processarFalha(fail: any) {
      this.toastr.warning(fail.error, 'Error!');
   }

   get f() {
      return this.form.controls;
   }

   pilares = [
      { label: 'Meritocracia', icon: 'fas fa-medal' },
      { label: 'Reconhecimento', icon: 'fas fa-bullhorn' },
      { label: 'Plano de Carreira', icon: 'fas fa-route' },
      { label: 'Celebramos Conquistas', icon: 'fas fa-bell' },
      { label: 'Programa Profissional Destaque', icon: 'fas fa-star' }
   ];

   selectedFile: File | null = null;

   onFileSelected(event: any) {
      const file = event.target.files[0];

      if (!file) {
         this.selectedFile = null;
         return;
      }

      // Verifica se é PDF
      if (file.type !== 'application/pdf') {
         this.selectedFile = null;
         this.toastr.warning('Por favor, envie apenas arquivos PDF.', 'Ops!');
         return;
      }

      // Verifica tamanho máximo (1,5 MB)
      const maxSizeInBytes = 1.5 * 1024 * 1024; // 1,5 MB
      if (file.size > maxSizeInBytes) {
         this.selectedFile = null;
         this.toastr.warning('Arquivo muito grande. O limite é 1,5 MB.', 'Ops!');
         return;
      }

      this.selectedFile = file;
   }
}
